import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { loginUser, logoutUser } from '../../database/firebase';
import { updatePassword, signOut } from 'firebase/auth';
import { auth } from '../../database/firebase';
import { getDocs, query, collection, where, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import ForgotPassword from './ForgotPassword';
import RegistrationPage from './RegistrationPage';
import ModeratorChangePassword from './ModeratorChangePassword';
import toast from 'react-hot-toast';
import LoginOtpInputPrompt from '../../components/auth/LoginOtpInputPrompt';

const initialForm = {
  email: '',
  password: '',
  rememberMe: false,
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
};

const toastVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
};

function mapAuthErrorToMessage(error) {
  const code = error?.code || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Invalid email or password. Please try again.';
    case 'auth/user-not-found':
      return 'No account found with that email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    default:
      return 'Unable to sign in right now. Please try again.';
  }
}

const LoginScreen = ({ open = true, onClose, onSubmit }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [moderatorUser, setModeratorUser] = useState(null);
  const [moderatorEmail, setModeratorEmail] = useState('');
  const [errorPopup, setErrorPopup] = useState({
    open: false,
    title: 'Sign in error',
    message: '',
  });
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { userRole, loading: roleLoading } = useUserRole();
  const [showOtpPrompt, setShowOtpPrompt] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [loginEmailForOtp, setLoginEmailForOtp] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');

  // Requires an authenticated Firebase user — Firestore rules reject unauthenticated reads.
  const checkOtpEnabled = async user => {
    if (!user?.email) return false;
    const email = user.email.toLowerCase().trim();
    const uid = user.uid;
    const flagOn = d => d?.loginOtpEnabled === true;

    try {
      const userSnap = await getDoc(doc(firestore, 'users', uid));
      if (userSnap.exists() && flagOn(userSnap.data())) return true;

      const clientSnap = await getDoc(doc(firestore, 'clients', uid));
      if (clientSnap.exists() && flagOn(clientSnap.data())) return true;

      const delegatedSnap = await getDoc(doc(firestore, 'delegatedUsers', uid));
      if (delegatedSnap.exists() && flagOn(delegatedSnap.data())) return true;

      const employeeSnap = await getDoc(doc(firestore, 'clientEmployees', uid));
      if (employeeSnap.exists() && flagOn(employeeSnap.data())) return true;

      const collections = ['coaches', 'moderator', 'admin', 's_admin'];

      for (const collectionName of collections) {
        const q = query(collection(firestore, collectionName), where('email', '==', email));
        const snapshot = await getDocs(q);
        if (!snapshot.empty && flagOn(snapshot.docs[0].data())) return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking OTP status:', error);
      return false;
    }
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleClose = () => {
    if (loading) return;
    setForm(initialForm);
    setShowPassword(false);
    setShowOtpPrompt(false);
    setOtpRequired(false);
    setOtpVerified(false);
    setLoginEmailForOtp('');
    setPendingPassword('');
    setErrorPopup({ ...errorPopup, open: false });
    if (onClose) onClose();
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
  };

  const handleShowRegistration = () => {
    setShowRegistration(true);
  };

  const handleBackFromRegistration = () => {
    setShowRegistration(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setErrorPopup({ ...errorPopup, open: false });

    try {
      const { email, password } = form;

      const user = await loginUser(email, password);
      if (!user) {
        throw new Error('Authentication failed');
      }

      const otpEnabled = await checkOtpEnabled(user);

      if (otpEnabled) {
        setLoginEmailForOtp(email);
        setPendingPassword(password);
        setOtpRequired(true);
        await signOut(auth);

        toast.success('Credentials verified. Sending OTP to your email...');
        setShowOtpPrompt(true);
        setLoading(false);
        return;
      }

      if (onSubmit) {
        onSubmit({ email, password });
      }

      toast.success('Login successful! Redirecting...');
      setLoading(false);
    } catch (error) {
      setLoading(false);
      const errorMessage = mapAuthErrorToMessage(error);
      setErrorPopup({
        open: true,
        title: 'Sign in error',
        message: errorMessage,
      });
    }
  };

  const handlePostAuthNavigation = async () => {
    if (!currentUser || roleLoading || !userRole) {
      return;
    }

    if (showOtpPrompt) {
      return;
    }

    if (otpRequired && !otpVerified) {
      return;
    }

    if (userRole === 'moderator') {
      try {
        const moderatorSnap = await getDocs(
          query(collection(firestore, 'moderator'), where('email', '==', currentUser.email))
        );
        if (moderatorSnap.size > 0) {
          const moderatorData = moderatorSnap.docs[0].data();
          if (moderatorData.mustChangePassword) {
            setModeratorUser(currentUser);
            setModeratorEmail(currentUser.email);
            setShowChangePassword(true);

            if (otpRequired) {
              setOtpRequired(false);
              setOtpVerified(false);
              setPendingPassword('');
              setLoginEmailForOtp('');
            }
            return;
          }
        }
      } catch (error) {
        console.error('Error checking moderator password change requirement:', error);
      }
    }

    try {
      const delegatedUserDoc = await getDoc(doc(firestore, 'delegatedUsers', currentUser.uid));
      if (delegatedUserDoc.exists()) {
        const delegatedData = delegatedUserDoc.data();
        const coachEmail = delegatedData.coachEmail;

        localStorage.setItem(
          'delegatedUser',
          JSON.stringify({
            userId: currentUser.uid,
            email: currentUser.email,
            coachId: delegatedData.coachId,
            coachEmail: coachEmail,
            permissions: delegatedData.permissions,
            userType: 'coach-delegated',
          })
        );

        toast.success('Welcome back! Redirecting...');
        navigate('/coach');

        // Clear OTP flags
        if (otpRequired) {
          setOtpRequired(false);
          setOtpVerified(false);
          setPendingPassword('');
          setLoginEmailForOtp('');
        }

        if (!otpRequired) {
          setShowToast(true);
          setTimeout(() => {
            setShowToast(false);
            setForm(initialForm);
            if (onClose) onClose();
          }, 2000);
        } else {
          setForm(initialForm);
          if (onClose) onClose();
        }

        return;
      }

      const clientEmployeeDoc = await getDoc(doc(firestore, 'clientEmployees', currentUser.uid));
      if (clientEmployeeDoc.exists()) {
        const employeeData = clientEmployeeDoc.data();
        const clientEmail = employeeData.clientEmail;
        const clientId = employeeData.clientId;

        let organizationId = null;
        try {
          const clientSnap = await getDocs(
            query(collection(firestore, 'clients'), where('email', '==', clientEmail))
          );
          if (clientSnap.size > 0) {
            const clientData = clientSnap.docs[0].data();
            organizationId = clientData.organization || clientData.organizationId;
          }
        } catch (error) {
          console.error('Error fetching organization:', error);
        }

        localStorage.setItem(
          'employeeUser',
          JSON.stringify({
            userId: currentUser.uid,
            email: currentUser.email,
            clientId: clientId,
            clientEmail: clientEmail,
            organizationId: organizationId,
            permissions: employeeData.permissions,
            userType: 'client-employee',
          })
        );

        toast.success('Welcome back! Redirecting...');
        navigate('/client');

        // Clear OTP flags
        if (otpRequired) {
          setOtpRequired(false);
          setOtpVerified(false);
          setPendingPassword('');
          setLoginEmailForOtp('');
        }

        if (!otpRequired) {
          setShowToast(true);
          setTimeout(() => {
            setShowToast(false);
            setForm(initialForm);
            if (onClose) onClose();
          }, 2000);
        } else {
          setForm(initialForm);
          if (onClose) onClose();
        }

        return;
      }
    } catch (error) {
      console.error('Error checking delegated/employee status:', error);
    }

    let targetRoute = '/client'; // default fallback

    switch (userRole) {
      case 'alumni':
        targetRoute = '/alumini';
        break;
      case 'coach':
      case 'businessCoach':
        targetRoute = '/coach';
        break;
      case 'client':
      case 'entrepreneur':
        targetRoute = '/client';
        break;
      case 'admin':
        targetRoute = '/admin';
        break;
      case 'moderator':
        targetRoute = '/moderator';
        break;
      case 'superAdmin':
        targetRoute = '/super-admin';
        break;
      case 'PlatformEngineer':
        targetRoute = '/PlatformEngineer';
        break;
      default:
        targetRoute = '/client';
    }

    toast.success('Welcome back!');
    navigate(targetRoute);

    if (otpRequired) {
      setOtpRequired(false);
      setOtpVerified(false);
      setPendingPassword('');
      setLoginEmailForOtp('');
    }

    if (!otpRequired) {
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setForm(initialForm);
        if (onClose) onClose();
      }, 2000);
    } else {
      setForm(initialForm);
      if (onClose) onClose();
    }
  };

  // Watch for authentication state changes and trigger navigation
  useEffect(() => {
    if (currentUser && !roleLoading && userRole) {
      handlePostAuthNavigation();
    }
  }, [currentUser, userRole, roleLoading, otpVerified]);

  if (!open) return null;

  return (
    <>
      <AnimatePresence mode="wait">
        {!showForgotPassword && !showRegistration && (
          <motion.div
            key="login-backdrop"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          >
            <motion.div
              key="login-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={`relative max-h-[90vh] w-[92%] max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl sm:w-full ${
                showOtpPrompt ? 'pointer-events-none opacity-30' : ''
              }`}
            >
              <AnimatePresence>
                {showToast && (
                  <motion.div
                    key="toast"
                    variants={toastVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="absolute left-1/2 top-4 z-50 -translate-x-1/2 transform rounded-lg bg-green-600 px-6 py-3 text-white shadow-lg"
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Login successful!
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="p-6 md:p-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.1 }}
                  className="absolute right-4 top-4 text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none disabled:opacity-50"
                  onClick={handleClose}
                  aria-label="Close"
                  disabled={loading}
                >
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </motion.button>

                <div className="mb-8 text-center">
                  <h2 className="mb-2 text-2xl font-bold text-blue-600 md:text-3xl">
                    Welcome Back
                  </h2>
                  <p className="text-sm text-gray-600 md:text-base">Sign in to your account</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="relative">
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                        <svg
                          width="18"
                          height="18"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <rect x="2" y="5" width="20" height="14" rx="2" />
                          <path d="m22 5-10 7L2 5" />
                        </svg>
                      </div>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={form.email}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-gray-300 bg-blue-50/50 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-all duration-150 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Enter your email address"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                        <svg
                          width="18"
                          height="18"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        id="password"
                        value={form.password}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-gray-300 bg-blue-50/50 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-all duration-150 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Enter your password"
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        disabled={loading}
                      >
                        {showPassword ? (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-10-8-10-8a21.77 21.77 0 0 1 5.06-6.94" />
                            <path d="M1 1l22 22" />
                            <path d="M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-.88" />
                            <path d="M14.12 14.12L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M1 12s3-8 11-8 11 8 11 8-3 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="rememberMe"
                        id="rememberMe"
                        checked={form.rememberMe}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-200"
                        disabled={loading}
                      />
                      <label htmlFor="rememberMe" className="text-sm text-gray-700">
                        Remember me
                      </label>
                    </div>
                    <motion.button
                      type="button"
                      onClick={handleForgotPassword}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      transition={{ duration: 0.1 }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      Forgot password?
                    </motion.button>
                  </div>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    transition={{ duration: 0.1 }}
                    className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-green-500 px-6 py-4 font-semibold text-white shadow-lg transition-all duration-150 hover:from-blue-600 hover:to-green-600 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 disabled:from-gray-400 disabled:to-gray-500 disabled:hover:scale-100"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-3">
                        <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        <span>Signing in...</span>
                      </div>
                    ) : (
                      <span className="text-lg">Sign In</span>
                    )}
                  </motion.button>

                  <motion.button
                    type="button"
                    onClick={handleShowRegistration}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    transition={{ duration: 0.1 }}
                    className="w-full text-center text-sm text-gray-600"
                  >
                    Don't have an account?{' '}
                    <span className="font-medium text-blue-600 hover:text-blue-800">Sign up</span>
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {errorPopup.open && (
          <motion.div
            key="error-backdrop"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          >
            <motion.div
              key="error-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:w-full"
              role="alertdialog"
              aria-labelledby="login-error-title"
              aria-describedby="login-error-desc"
            >
              <div className="mb-4 flex items-start gap-3">
                <div className="mt-0.5 text-red-600">
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </div>
                <div>
                  <h3 id="login-error-title" className="text-lg font-semibold text-gray-900">
                    {errorPopup.title}
                  </h3>
                  <p id="login-error-desc" className="mt-1 text-sm text-gray-700">
                    {errorPopup.message}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setErrorPopup({ ...errorPopup, open: false })}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  OK
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ForgotPassword
        open={showForgotPassword}
        onClose={handleClose}
        onSubmit={formData => {
          toast.info('Reset password for:', formData.email);
        }}
        onBackToLogin={handleBackToLogin}
      />
      <RegistrationPage
        open={showRegistration}
        onClose={handleBackFromRegistration}
        onSubmit={formData => {}}
      />
      <ModeratorChangePassword
        open={showChangePassword}
        email={moderatorEmail}
        onClose={() => setShowChangePassword(false)}
        onPasswordChange={async newPassword => {
          if (!moderatorUser) return;

          try {
            await updatePassword(moderatorUser, newPassword);

            const moderatorSnap = await getDocs(
              query(collection(firestore, 'moderator'), where('email', '==', moderatorEmail))
            );
            if (!moderatorSnap.empty) {
              const docRef = doc(firestore, 'moderator', moderatorSnap.docs[0].id);
              await updateDoc(docRef, { mustChangePassword: false });
            }
            localStorage.removeItem('mod_temp_pw');
          } catch (error) {
            toast.error('Password change error:', error);
            throw error;
          }
        }}
        onSuccess={() => {
          setShowChangePassword(false);
          navigate('/moderator');
        }}
      />
      {/* Login OTP Input Popover */}
      <LoginOtpInputPrompt
        email={loginEmailForOtp}
        open={showOtpPrompt}
        onOtpVerified={async verificationResult => {
          try {
            // If OTP was skipped (should not happen as we check isOTPEnabled first)
            if (verificationResult && verificationResult.skipped) {
              const user = await loginUser(loginEmailForOtp, pendingPassword);
              if (!user) {
                throw new Error('Authentication failed');
              }
              setOtpVerified(true);
              setShowOtpPrompt(false);
              setOtpRequired(false);
              setPendingPassword('');
              setLoginEmailForOtp('');
              toast.success('Login successful! Redirecting...');
              return;
            }

            // OTP was successfully verified - re-authenticate user
            if (!pendingPassword) {
              throw new Error('Password not available for re-authentication');
            }

            const user = await loginUser(loginEmailForOtp, pendingPassword);

            if (!user) {
              throw new Error('Authentication failed');
            }

            // Wait for AuthContext to update
            await new Promise(resolve => setTimeout(resolve, 100));

            setOtpVerified(true);
            setShowOtpPrompt(false);

            toast.success('Verification successful! Redirecting...');

            // Clear sensitive data
            setPendingPassword('');
            setLoginEmailForOtp('');
          } catch (error) {
            const errorMessage =
              error?.code === 'auth/invalid-credential' || error?.code === 'auth/wrong-password'
                ? 'Invalid email or password.'
                : error?.message || 'Failed to complete login. Please try again.';
            toast.error(errorMessage);
            setOtpVerified(false);
            setShowOtpPrompt(false);
            setOtpRequired(false);
            setPendingPassword('');
            setLoginEmailForOtp('');
            try {
              await signOut(auth);
            } catch (logoutError) {
              console.error('Logout error:', logoutError);
            }
          }
        }}
        onCancel={async () => {
          setShowOtpPrompt(false);
          setOtpRequired(false);
          setOtpVerified(false);
          setPendingPassword('');
          setLoginEmailForOtp('');
          try {
            await signOut(auth);
          } catch (e) {
            console.error('Logout error:', e);
          }
          toast.error('You must complete OTP verification to finish signing in.');
        }}
      />
    </>
  );
};

export default LoginScreen;
